import { Static, TLiteral, TUnion, Type } from '@sinclair/typebox';

// =====================================================================
// Migration des boîtes mail OVH (Exchange / Email Pro) vers Stalwart — contrats partagés
// backend ⇄ admin-web. Cf. docs/to_implement/email-ovh-exchange-migration-plan.md.
//
// ⚠️ Miroir des enums Prisma `ProMailMigrationStatus` / `ProMailMigrationMailboxStatus` /
// `OvhMailOffer` (packages/backend/prisma-pg/schema.prisma) — garder l'ordre et les valeurs synchro.
// =====================================================================

/**
 * État d'une migration (1 par domaine, toutes ses boîtes partent ensemble). L'ordre reflète §3 du
 * plan : DNS basculé AVANT la copie des messages.
 */
export enum ProMailMigrationStatus {
  /** Domaine repéré (liste héritée ou inventaire OVH), rien n'a été vérifié. */
  CANDIDATE = 'CANDIDATE',
  /** « Préparer » : tous les prérequis sont verts, TTL abaissé — « Migrer » est possible. */
  READY = 'READY',
  /** « Préparer » : au moins un prérequis bloquant (détail dans `checks`). */
  BLOCKED = 'BLOCKED',
  /** Job lancé : domaine + boîtes Stalwart, rows `pro_mailbox`, reset des mots de passe OVH. */
  PROVISIONING = 'PROVISIONING',
  /** DNS poussé chez OVH, en attente de propagation du MX. */
  DNS_PENDING = 'DNS_PENDING',
  /** Copie IMAP complète en cours (puis une recopie incrémentale). */
  SYNCING = 'SYNCING',
  /** Copie terminée, dernière recopie différée au lendemain matin. */
  FINAL_SYNC_SCHEDULED = 'FINAL_SYNC_SCHEDULED',
  /** Terminé : suppression manuelle des comptes OVH possible à J+30. */
  DONE = 'DONE',
  /** Échec (détail dans `last_error`), reprise possible par « Réessayer ». */
  ERROR = 'ERROR',
  /**
   * « Annuler la migration » demandé : le job de retour arrière tourne (le job de migration en vol
   * s'arrête au prochain lot, le DNS mail n'est plus publié par le provisioning).
   */
  ROLLING_BACK = 'ROLLING_BACK',
  /** « Annuler la migration » : DNS d'origine reposé, rows soft-deleted, courrier recopié vers OVH. */
  ROLLED_BACK = 'ROLLED_BACK',
}

/** État de la copie d'une boîte OVH. */
export enum ProMailMigrationMailboxStatus {
  PENDING = 'PENDING',
  SYNCING = 'SYNCING',
  SYNCED = 'SYNCED',
  ERROR = 'ERROR',
}

/** Offre mail OVH d'origine (hôte IMAP `exN.mail.ovh.net` / `proN.mail.ovh.net`). */
export enum OvhMailOffer {
  EXCHANGE = 'EXCHANGE',
  EMAIL_PRO = 'EMAIL_PRO',
}

/** Prérequis contrôlés par « Préparer » (§3.1) — tous doivent être verts pour « Migrer ». */
export enum MailMigrationCheckKey {
  /** Domaine vivant côté site (row `pro_website_domain` non supprimée). */
  DOMAIN_ALIVE = 'DOMAIN_ALIVE',
  /** Pro v2 actif (abonnement en cours, pas un compte supprimé). */
  PRO_ACTIVE = 'PRO_ACTIVE',
  /** Tout email de connexion / de contact sur ce domaine a une adresse de récupération vérifiée (règle 28 §1). */
  RECOVERY_EMAILS = 'RECOVERY_EMAILS',
  /** Inventaire OVH à jour (< 24 h) et au moins une boîte. */
  INVENTORY_FRESH = 'INVENTORY_FRESH',
  /** Offre et hôte IMAP OVH connus. */
  OVH_HOST_KNOWN = 'OVH_HOST_KNOWN',
  /** Aucune boîte au-dessus du quota maximal d'une boîte chez nous. */
  MAILBOX_SIZE = 'MAILBOX_SIZE',
  /** Stalwart configuré sur cet environnement. */
  STALWART_CONFIGURED = 'STALWART_CONFIGURED',
  /** Clés OVH de migration configurées. */
  OVH_CLIENT_CONFIGURED = 'OVH_CLIENT_CONFIGURED',
  /**
   * Master user IMAP Stalwart configuré (`STALWART_IMAP_MASTER_*`) : les recopies (finale, à la
   * demande, inverse) lisent les boîtes sans toucher au mot de passe que le pro a choisi.
   */
  STALWART_MASTER_CONFIGURED = 'STALWART_MASTER_CONFIGURED',
  /** TTL des records mail OVH existants abaissé à 300 s. */
  TTL_LOWERED = 'TTL_LOWERED',
}

/** Avertissements non bloquants (§3.1, tranché 23/10) : à dire au pro, jamais une condition. */
export enum MailMigrationWarningKey {
  /** Alias d'adresse : non repris tant que `x:EmailAlias` n'existe pas. */
  ALIASES = 'ALIASES',
  /** Boîtes partagées Exchange : non reprises. */
  SHARED_MAILBOXES = 'SHARED_MAILBOXES',
  /** Groupes de distribution : non repris. */
  MAILING_LISTS = 'MAILING_LISTS',
  /** Comptes ressources (salles, matériel) : non repris. */
  RESOURCE_ACCOUNTS = 'RESOURCE_ACCOUNTS',
  /** Contacts / agendas / tâches : indétectables par API, avertissement systématique. */
  CONTACTS_CALENDARS = 'CONTACTS_CALENDARS',
}

/** Union de littéraux typée sur les valeurs de l'enum (le `Static` reste l'enum, pas `unknown`). */
const enumLiteralUnion = <T extends string>(e: Record<string, T>): TUnion<TLiteral<T>[]> =>
  Type.Union(Object.values(e).map((v): TLiteral<T> => Type.Literal(v)));

/** Statuts depuis lesquels « Annuler la migration » est possible (tant que les comptes OVH existent). */
export const MAIL_MIGRATION_ROLLBACKABLE_STATUSES: readonly ProMailMigrationStatus[] = [
  ProMailMigrationStatus.PROVISIONING,
  ProMailMigrationStatus.DNS_PENDING,
  ProMailMigrationStatus.SYNCING,
  ProMailMigrationStatus.FINAL_SYNC_SCHEDULED,
  ProMailMigrationStatus.DONE,
  ProMailMigrationStatus.ERROR,
  // Relance possible si le worker a redémarré pendant le retour arrière (job idempotent).
  ProMailMigrationStatus.ROLLING_BACK,
];

/**
 * Statuts où le DNS mail du domaine pointe chez nous (bascule faite) : la création de boîte en
 * self-service redevient possible sur un domaine hérité d'OVH.
 */
export const MAIL_MIGRATION_SWITCHED_STATUSES: readonly ProMailMigrationStatus[] = [
  ProMailMigrationStatus.DNS_PENDING,
  ProMailMigrationStatus.SYNCING,
  ProMailMigrationStatus.FINAL_SYNC_SCHEDULED,
  ProMailMigrationStatus.DONE,
];

/** Statuts « en cours » : la page admin les rafraîchit par polling. */
export const MAIL_MIGRATION_IN_PROGRESS_STATUSES: readonly ProMailMigrationStatus[] = [
  ProMailMigrationStatus.PROVISIONING,
  ProMailMigrationStatus.DNS_PENDING,
  ProMailMigrationStatus.SYNCING,
  ProMailMigrationStatus.ROLLING_BACK,
];

// ── Réponses admin ──

export const AdminMailMigrationCheckSchema = Type.Object({
  key: enumLiteralUnion(MailMigrationCheckKey),
  ok: Type.Boolean(),
  /** Détail bloquant lisible (ex. adresses sans récupération), vide si ok. */
  detail: Type.Optional(Type.String()),
});
export type AdminMailMigrationCheck = Static<typeof AdminMailMigrationCheckSchema>;

export const AdminMailMigrationWarningSchema = Type.Object({
  key: enumLiteralUnion(MailMigrationWarningKey),
  /** Éléments nommés (alias, boîtes partagées…) ; vide pour l'avertissement systématique. */
  items: Type.Array(Type.String()),
});
export type AdminMailMigrationWarning = Static<typeof AdminMailMigrationWarningSchema>;

export const AdminMailMigrationMailboxSchema = Type.Object({
  pro_mail_migration_mailbox_id: Type.Number(),
  ovh_email: Type.String(),
  /** Row `pro_mailbox` créée à la provision (absent avant). */
  pro_mailbox_id: Type.Optional(Type.Number()),
  status: enumLiteralUnion(ProMailMigrationMailboxStatus),
  /** Volume OVH lisible (« 1,2 Go »), absent si inconnu. */
  ovh_usage_formatted: Type.Optional(Type.String()),
  ovh_last_logon_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'ovh_last_logon_at', with_time: true } }),
  ),
  aliases: Type.Array(Type.String()),
  messages_source: Type.Number(),
  messages_copied: Type.Number(),
  last_sync_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_sync_at', with_time: true } }),
  ),
  last_error: Type.Optional(Type.String()),
  /** Journal borné de la copie, dossier par dossier. */
  log_tail: Type.Optional(Type.String()),
});
export type AdminMailMigrationMailbox = Static<typeof AdminMailMigrationMailboxSchema>;

export const AdminMailMigrationListItemSchema = Type.Object({
  pro_mail_migration_id: Type.Number(),
  pro_website_domain_id: Type.Number(),
  domain: Type.String(),
  professional_id: Type.Optional(Type.Number()),
  professional_name: Type.Optional(Type.String()),
  status: enumLiteralUnion(ProMailMigrationStatus),
  ovh_offer: Type.Optional(enumLiteralUnion(OvhMailOffer)),
  ovh_service: Type.Optional(Type.String()),
  mailbox_count: Type.Number(),
  /** Volume total OVH lisible, absent sans inventaire. */
  usage_formatted: Type.Optional(Type.String()),
  /** Prérequis : nombre de bloquants et d'avertissements (détail dans la fiche). */
  blocking_count: Type.Number(),
  warning_count: Type.Number(),
  scheduled_for_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'scheduled_for', with_time: true } }),
  ),
  inventory_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'inventory_at', with_time: true } }),
  ),
  started_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'started_at', with_time: true } }),
  ),
  finished_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'finished_at', with_time: true } }),
  ),
  last_error: Type.Optional(Type.String()),
  updated_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
});
export type AdminMailMigrationListItem = Static<typeof AdminMailMigrationListItemSchema>;

export const AdminMailMigrationListResponseSchema = Type.Object({
  items: Type.Array(AdminMailMigrationListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
  /** Compteurs par statut sur l'ensemble (hors filtres), pour les tuiles. */
  counts: Type.Record(Type.String(), Type.Number()),
});
export type AdminMailMigrationListResponse = Static<typeof AdminMailMigrationListResponseSchema>;

/** Un record DNS mail prévu (diff « zone actuelle vs cible » de la fiche). */
export const AdminMailMigrationDnsRecordSchema = Type.Object({
  type: Type.String(),
  name: Type.String(),
  value: Type.String(),
});

export const AdminMailMigrationDetailSchema = Type.Composite([
  AdminMailMigrationListItemSchema,
  Type.Object({
    ovh_org: Type.Optional(Type.String()),
    ovh_imap_host: Type.Optional(Type.String()),
    csm_note: Type.Optional(Type.String()),
    checks: Type.Array(AdminMailMigrationCheckSchema),
    warnings: Type.Array(AdminMailMigrationWarningSchema),
    mailboxes: Type.Array(AdminMailMigrationMailboxSchema),
    /** Vrai dès que l'instantané DNS a été pris (condition du retour arrière). */
    dns_snapshot_taken: Type.Boolean(),
    /** Records mail que le job posera (hors DKIM, lu chez Stalwart au moment du job). */
    dns_plan: Type.Array(AdminMailMigrationDnsRecordSchema),
    /** Records mail actuellement dans la zone OVH (lecture à la demande, vide hors prod). */
    dns_current: Type.Array(AdminMailMigrationDnsRecordSchema),
    launched_by_admin_id: Type.Optional(Type.Number()),
    rolled_back_at_formatted: Type.Optional(
      Type.String({ format: 'date-time', meta: { source_key: 'rolled_back_at', with_time: true } }),
    ),
    /** Statut du domaine mail Stalwart (`pro_mail_domain`), absent avant la provision. */
    mail_domain_status: Type.Optional(Type.String()),
    /** Retour arrière terminé : un mot de passe OVH de remise attend d'être révélé (une fois). */
    rollback_password_available: Type.Boolean(),
  }),
]);
export type AdminMailMigrationDetail = Static<typeof AdminMailMigrationDetailSchema>;

// ── Requêtes admin ──

/** PUT /app/admin/mail-migration/:id/schedule — date de bascule prévue + note de pilotage. */
export const AdminMailMigrationScheduleBodySchema = Type.Object({
  /** Soirée de bascule prévue (null = retirer). */
  scheduled_for: Type.Optional(Type.Union([Type.String({ format: 'date-time' }), Type.Null()])),
  /** Note libre côté pilotage (« pro prévenu le … »). */
  csm_note: Type.Optional(Type.Union([Type.String({ maxLength: 2000 }), Type.Null()])),
});
export type AdminMailMigrationScheduleBody = Static<typeof AdminMailMigrationScheduleBodySchema>;

/** POST /app/admin/mail-migration/inventory — inventaire OVH enqueue. */
export const AdminMailMigrationInventoryResponseSchema = Type.Object({ success: Type.Boolean() });
export type AdminMailMigrationInventoryResponse = Static<typeof AdminMailMigrationInventoryResponseSchema>;

export const AdminMailMigrationActionResponseSchema = Type.Object({
  success: Type.Boolean(),
  status: enumLiteralUnion(ProMailMigrationStatus),
  /**
   * Retour arrière uniquement : nouveau mot de passe OVH des boîtes, affiché UNE fois à l'admin
   * (jamais stocké), à transmettre au pro.
   */
  ovh_password: Type.Optional(Type.String()),
});
export type AdminMailMigrationActionResponse = Static<typeof AdminMailMigrationActionResponseSchema>;
